import { useEffect, useState } from 'react'
import { router } from '@inertiajs/react'
import { Loader2, ScanLine } from 'lucide-react'

import BarcodeScanner from '@/shared/components/BarcodeScanner'
import { Button } from '@/shared/ui/button'
import { t } from '@/shared/lib/i18n'
import { byBarcodeInstrumentsPath, instrumentPath } from '@/shared/lib/routes'
import { getCsrfToken } from '@/shared/lib/utils'

interface HeaderScanButtonProps {
  mobile?: boolean
}

const HeaderScanButton = ({ mobile = false }: HeaderScanButtonProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [scanError, setScanError] = useState<string | null>(null)

  useEffect(() => {
    if (!scanError) return
    const dismissTimer = setTimeout(() => setScanError(null), 5000)
    return () => clearTimeout(dismissTimer)
  }, [scanError])

  const handleScan = async (barcode: string) => {
    setIsLoading(true)
    setScanError(null)

    try {
      const response = await fetch(
        `${byBarcodeInstrumentsPath()}?barcode=${encodeURIComponent(barcode)}`,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-CSRF-Token': getCsrfToken(),
          },
        },
      )

      if (response.ok) {
        const data = (await response.json()) as { id: number; instrument_label: string }
        if (!data?.id) {
          setScanError(t('barcodeScanner.lookupError'))
        } else {
          router.visit(instrumentPath(data.id))
        }
      } else if (response.status === 404) {
        setScanError(t('barcodeScanner.notFound'))
      } else if (response.status === 422) {
        setScanError(t('barcodeScanner.multipleFound'))
      } else {
        setScanError(t('barcodeScanner.lookupError'))
      }
    } catch {
      setScanError(t('barcodeScanner.lookupError'))
    } finally {
      setIsLoading(false)
    }
  }

  const triggerButton = mobile ? (
    <Button
      type="button"
      variant="ghost"
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[15px] leading-snug transition-colors text-foreground/90 hover:bg-muted/80 active:bg-muted/90 h-auto justify-start font-normal"
      disabled={isLoading}
    >
      {isLoading ? (
        <Loader2 className="size-5 shrink-0 animate-spin text-muted-foreground" aria-hidden />
      ) : (
        <ScanLine className="size-5 shrink-0 text-muted-foreground" aria-hidden />
      )}
      <span className="min-w-0 flex-1 leading-snug">{t('nav.scanBarcode')}</span>
    </Button>
  ) : (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="shrink-0 text-white/70 hover:bg-white/10 hover:text-white"
      aria-label={t('barcodeScanner.scanButton')}
      disabled={isLoading}
    >
      {isLoading ? (
        <Loader2 className="size-4 animate-spin" aria-hidden />
      ) : (
        <ScanLine className="size-4" aria-hidden />
      )}
    </Button>
  )

  const errorMessage = scanError && (
    <p role="alert" className="text-xs text-destructive mt-1">
      {scanError}
    </p>
  )

  if (mobile) {
    return (
      <div>
        <BarcodeScanner onScan={handleScan} trigger={triggerButton} />
        {errorMessage}
      </div>
    )
  }

  return (
    <div className="relative">
      <BarcodeScanner onScan={handleScan} trigger={triggerButton} />
      {scanError && (
        <div
          role="alert"
          className="absolute top-full right-0 z-50 mt-1 max-w-[200px] rounded-md bg-destructive px-3 py-2 text-xs text-destructive-foreground shadow-md"
        >
          {scanError}
        </div>
      )}
    </div>
  )
}

export default HeaderScanButton
